import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  Box,
  Grid2,
  MenuItem,
  Select,
  Skeleton,
  Typography,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { IoIosArrowDown } from "react-icons/io";
import { useParams } from "react-router-dom";
import { useGetProductivityMutation } from "store/slices/apis/manpowerDashboardSlice";
import { ReactComponent as ChevronDownIcon } from "assets/PwC_Funct_Icons_ChevronDown_Outline_Black_RGB.svg"

const colorPalette = [
  '#BFD0CA', '#423C3E', '#6E5731', '#8B2F22',
  '#54729A', '#124064', '#E87536', '#343450',
  '#567249', '#4B9637', '#E9B83A', '#34457D',
  '#AF583C', '#436A90', '#47678D', '#F5B61B'
];

const ManpowerProductivity = () => {
  const { projectId } = useParams();
  const theme = useTheme();
  const [getProductivity] = useGetProductivityMutation();

  const [allData, setAllData] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [chartMonths, setChartMonths] = useState([]);
  const [chartData, setChartData] = useState({
    planned: [],
    actual: [],
    plannedColor: "#415385",
    actualColor: "#6E5731",
  });
  const [selectedYear, setSelectedYear] = useState("2025");
  const [loading, setLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const res = await getProductivity({
          projectId,
          year: selectedYear,
          body: [],
        }).unwrap();

        const rawData = res.data || [];
        setAllData(rawData);

        if (rawData.length === 0) {
          setAllCategories(["No Category"]);
          setSelectedCategory("No Category");
          updateChart([]);
          setLoading(false);
          return;
        }

        const categories = [...new Set(rawData.map((item) => item.category))];
        setAllCategories(categories);

        let finalCategory = selectedCategory;

        if (!finalCategory || !categories.includes(finalCategory)) {
          if (selectedCategory) {
            setPopupMessage(`Please select a valid category. "${selectedCategory}" is not available in year ${selectedYear}.`);
            setPopupOpen(true);
          }
          finalCategory = categories[0];
        }

        setSelectedCategory(finalCategory);
        const filtered = rawData.filter((item) => item.category === finalCategory);
        updateChart(filtered, categories);
        setLoading(false);
      } catch (err) {
        console.log("error", err);
        setAllCategories(["No Category"]);
        setSelectedCategory("No Category");
        updateChart([]);
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [projectId, selectedYear]);

  const updateChart = (filteredData, categoryList = allCategories) => {
    if (!filteredData || filteredData.length === 0) {
      setChartMonths(["No Data"]);
      setChartData({
        planned: [0],
        actual: [0],
        plannedColor: "#cccccc",
        actualColor: "#999999"
      });
      return;
    }

    const months = filteredData.map((item) => item.month);
    const planned = filteredData.map((item) => Number(item.plannedProductivity.toFixed(2)));
    const actual = filteredData.map((item) => Number(item.actualProductivity.toFixed(2)));

    const catIndex = categoryList.indexOf(filteredData[0].category);
    const plannedColor = colorPalette[(catIndex * 2) % colorPalette.length];
    const actualColor = colorPalette[(catIndex * 2 + 1) % colorPalette.length];



    setChartMonths(months);
    setChartData({
      planned,
      actual,
      plannedColor,
      actualColor,
    });
  };

  const handleCategoryChange = (event) => {
    const cat = event.target.value;
    setSelectedCategory(cat);
    const filtered = allData.filter((item) => item.category === cat);
    updateChart(filtered);
  };

  const chartOptions = {
    chart: {
      // height: 500
    },
    title: {
      text:
        chartMonths.length === 1 && chartMonths[0] === "No Data"
          ? "No Record Found"
          : "",
    },
    xAxis: {
      categories: chartMonths,
      labels: {
        useHTML: true,
        style: {
          color: "#00000080",
          fontSize: "12px",
          fontWeight: 500,
        },
        formatter: function () {
          return `<div style="text-align:center;">
                    <div style="font-weight:600;">${selectedCategory}</div>
                    <div>${this.value}</div>
                  </div>`;
        },
      },
      lineWidth: 1,
      tickWidth: 1,
      tickmarkPlacement: "on",
    },
    yAxis: {
      title: {
        text: "Productivity in %",
        style: {
          color: "#415385",
          fontSize: "14px",
          fontWeight: 500,
        }
      },
      labels: {
        style: {
          color: "#464646",
          fontSize: "10px",
          fontWeight: 500,
        },
      },
      gridLineWidth: 0,
      lineWidth: 1,
      tickWidth: 1,
      tickmarkPlacement: "on",
    },
    legend: {
      enabled: true,
      symbolHeight: 17,
      symbolWidth: 17,
      symbolRadius: 0,
      align: "left",
      x: 20,
      itemStyle: {
        fontWeight: 500,
        color: "#00000080",
        fontSize: "15px",
      },
    },
    tooltip: {
      shared: false,
      headerFormat: "<b>{point.key}</b><br/>",
      pointFormat: "{series.name}: {point.y}<br/>",
    },
    plotOptions: {
      column: {
        borderRadius: 5,
        dataLabels: {
          enabled: true,
          format: "{point.y}",
          style: {
            fontSize: "12px",
            fontWeight: 500,
          },
        },
      },
    },
    credits: { enabled: false },
    series: [
      {
        type: "column",
        name: "Planned",
        data: chartData.planned,
        color: chartData.plannedColor,
      },
      {
        type: "column",
        name: "Actual",
        data: chartData.actual,
        color: chartData.actualColor,
      },
    ],
  };

   const CustomChevronIcon = (props) => (
                <ChevronDownIcon style={{ width: '16px', height: '16px' }} {...props} />
            );

  return (
    <Box sx={{ padding: 2, background: "#fff", borderRadius: 2, height: "100%", border: "1px solid #2525252E" }}>
      <Grid2 container>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Typography
            variant="body"
            sx={{
              fontWeight: 500,
              fontSize: "18px",
              lineHeight: "100%",
              letterSpacing: "-0.4px",
              color: `${theme?.customSettings?.themeColor}`,
            }}
          >
            Manpower Productivity
          </Typography>
          {/* <Typography
            sx={{
              mt: "5px",
              color: "#5052588A",
              fontSize: "14px",
              mb: "20px",
              lineHeight: "100%",
              letterSpacing: "-0.4px",
            }}
          >
            Get a holistic view of how work is being prioritized
          </Typography> */}
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              width: '100%',
              display: "flex",
              justifyContent: { xs: "flex-start", md: "flex-end" },
              flexDirection: { xs: "column", md: "row" },
              mt: "14px",
              gap: { xs: 2, md: 0 },
            }}
          >
            <Select
              value={selectedCategory || ""}
              onChange={handleCategoryChange}
              // IconComponent={IoIosArrowDown}
              IconComponent={CustomChevronIcon}
              disabled={allCategories.length === 1 && allCategories[0] === "No Category"}
              sx={{
                marginLeft: { xs: 0, md: "30px" },
                backgroundColor: "#E8E8E8",
                color: "#474747",
                boxShadow: "none",
                "& .MuiOutlinedInput-input": { padding: "5px 15px" },
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              }}
            >
              {allCategories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>

            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              // IconComponent={IoIosArrowDown}
              IconComponent={CustomChevronIcon}
              sx={{
                marginLeft: { xs: 0, md: "30px" },
                backgroundColor: "#E8E8E8",
                color: "#474747",
                boxShadow: "none",
                "& .MuiOutlinedInput-input": { padding: "5px 15px" },
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              }}
            >
              <MenuItem value="2025">2025</MenuItem>
              <MenuItem value="2024">2024</MenuItem>
            </Select>
          </Box>
        </Grid2>
      </Grid2>

      <Grid2 xs={12} sx={{ mt: "70px" }}>
        {loading ? (
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2, my: 2 }} />
        ) : chartMonths.length === 1 && chartMonths[0] === "No Data" ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 400,
              mt: 2,
              color: "#9E9E9E",
              fontSize: "16px",
            }}
          >
            No data available to display the chart.
          </Box>
        ) : (
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        )}
      </Grid2>

      <Dialog open={popupOpen} onClose={() => setPopupOpen(false)}>
        <DialogTitle>Invalid Category</DialogTitle>
        <DialogContent>
          <Typography>{popupMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPopupOpen(false)} variant="contained" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManpowerProductivity;
